import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { history } from '../../utils';
import GeneralUserInfo from './userDetailsAssets/generalUserInfo';
import { ISystem } from '../../ts/interface/system.interface';
import PageContainer from '../../components/Redesing/page-container';
import UserDetailsSkeleton from './userDetailsAssets/UserDetailsSkeleton';
import { useSystemStore } from '../../store/systemStore';
import useOrganization from '../../hooks/use-organization';
import {
  useUserByPhoneQuery,
  mapRelayUserDetailToIUserDetail,
  mapRelayUserDetailToIUserOrganizationDetails,
  mapRelayErrorReportsToProblem,
  mapRelayUserMembershipsToIUserMemberships,
  mapRelayRfidCardsToIRfidCards,
} from '../../hooks/users/query/V2';
import type { IUserMembership } from '../../ts/interface/membership.interface';
import type { IRfidCard } from '../../ts/interface/rfidCard.interface.tsx';
import type {
  IUserDetail,
  IUserOrganizationDetails,
} from '../../ts/interface/userDetail.interface';
import type { IProblem } from '../../ts/interface/problem.interface';

const UserDetails = ({
  system,
  unitsTitle,
  systemLoaded,
}: {
  system: ISystem;
  unitsTitle: string;
  systemLoaded: boolean;
}) => {
  const { userPhone = '' } = useParams<{
    userPhone: string;
  }>();
  const systemGid = useSystemStore((state) => state.systemInfo.gid);
  const organizationInfo = useOrganization();
  const organizationId = organizationInfo.id || '';
  const isOrgContext = Boolean(organizationId);

  const enabled = isOrgContext
    ? Boolean(userPhone)
    : systemLoaded && Boolean(userPhone);

  const {
    isLoading,
    refetch: userRefetch,
    data: userData,
    organizationSystems,
  } = useUserByPhoneQuery(userPhone, {
    enabled,
    onError: () => history.push('/users'),
    systemId: isOrgContext ? undefined : systemGid,
    organizationId: isOrgContext ? organizationId : undefined,
    partnerOldId: isOrgContext ? undefined : system.partner,
  });

  // Map GraphQL data to the appropriate legacy interface (system vs org context)
  const userDetails = useMemo<IUserDetail | IUserOrganizationDetails>(() => {
    if (!userData) return {} as IUserDetail;
    if (isOrgContext) {
      return mapRelayUserDetailToIUserOrganizationDetails(
        userData,
        organizationId,
        organizationSystems
      );
    }
    return mapRelayUserDetailToIUserDetail(
      userData,
      systemGid,
      undefined,
      system.partner
    );
  }, [
    userData,
    systemGid,
    isOrgContext,
    organizationId,
    organizationSystems,
    system.partner,
  ]);

  // Map error reports to problems
  const userProblems = useMemo<IProblem[]>(() => {
    if (!userData?.errorReports) return [];
    return mapRelayErrorReportsToProblem(userData.errorReports, userData.fullName ?? '');
  }, [userData]);

  // Map user memberships from GraphQL
  const userMemberships = useMemo<IUserMembership[]>(() => {
    if (!userData?.userMemberships) return [];
    return mapRelayUserMembershipsToIUserMemberships(userData.userMemberships);
  }, [userData]);

  // Map RFID cards from GraphQL
  const userRfidCards = useMemo<IRfidCard[]>(() => {
    if (!userData?.rfidCards) return [];
    return mapRelayRfidCardsToIRfidCards(userData.rfidCards);
  }, [userData]);

  // Create a compatible user object for legacy components
  const user = useMemo(() => {
    if (!userData) return null;
    return {
      userDetails,
      problems: userProblems,
      memberships: userMemberships,
      rfidCards: userRfidCards,
    };
  }, [userData, userDetails, userProblems, userMemberships, userRfidCards]);

  // Show skeleton while loading initial data
  if (isLoading || !userDetails.id) {
    return <UserDetailsSkeleton />;
  }

  // In org context we pass the per-user organization systems as an ISystem[] cast —
  // this matches the legacy shape the banner already handles for arrays.
  const systemProp: ISystem | ISystem[] = isOrgContext
    ? (((userDetails as IUserOrganizationDetails).systems ?? []) as unknown as ISystem[])
    : system;
  const privateSystem = isOrgContext ? false : system.privateSystem;
  const usesWallet = isOrgContext ? false : system.wallet_required;

  return (
    <PageContainer isLoading={false}>
      <GeneralUserInfo
        unitsTitle={unitsTitle}
        user={user}
        isLoading={isLoading}
        userDetails={userDetails}
        privateSystem={privateSystem}
        onSuccess={() => {
          userRefetch();
        }}
        usesWallet={usesWallet}
        system={systemProp}
        problems={userProblems}
        systemGid={systemGid}
        creditCards={userData?.creditCards as any[] | undefined}
        userDelinquent={userData?.delinquent ?? false}
        organizationId={organizationId || undefined}
        isOrgContext={isOrgContext}
      />
    </PageContainer>
  );
};

const UserDetailR = connect(
  (state: {
    system: { current: ISystem; isLoaded: boolean };
    mode: { details: { unitsTitle: string } };
  }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    unitsTitle: state.mode.details.unitsTitle,
  }),
  () => ({})
)(UserDetails);

export default UserDetailR;
